import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Stack,
} from '@chakra-ui/react';
import * as React from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { AllowedViewers } from '@/components/AllowedViewers';
import { EAttributeValidationRule } from '@/graphql-types/globalTypes';
import { useCurrent } from '@/hooks/useCurrent';
import {
  EditableAttribute,
  FreeText,
  MultiSelect,
  SingleSelect,
  attributeValidationRules,
} from '@/modules/attributes';

export const Edit: React.FC<{
  autoFocusAttributeId?: string;
  isViewingOwnProfile: boolean;
}> = ({ autoFocusAttributeId, isViewingOwnProfile }) => {
  const current = useCurrent();
  const association = current.tenant?.acronym ?? current.tenant?.displayName;

  const {
    control,
    formState: { isSubmitting },
  } = useFormContext<{ attributes: EditableAttribute[] }>();

  const fieldArray = useFieldArray({ name: 'attributes', control });

  return (
    <Stack spacing="6">
      {fieldArray.fields.map((field, index) => {
        const isAllowedViewersVisible =
          !field.isControlledByExternalAMSField && field.isOnProfile;

        return (
          <Controller
            key={field.id}
            name={`attributes.${index}.value`}
            control={control}
            rules={{
              validate: field.canCurrentUserEditValue
                ? attributeValidationRules({
                    validationRules: field.validationRules,
                    label: field.label,
                    initialValue: field.value,
                  })
                : undefined,
            }}
            render={({
              field: { value, onChange, onBlur, ref },
              fieldState: { error },
            }) => (
              <FormControl
                isInvalid={!!error}
                isDisabled={isSubmitting || !field.canCurrentUserEditValue}
                isRequired={
                  field.canCurrentUserEditValue &&
                  field.validationRules.includes(
                    EAttributeValidationRule.REQUIRED
                  )
                }
              >
                <FormLabel m="0" flex="1">
                  {field.label}
                </FormLabel>

                {field.__typename === 'MultiSelectAttribute' &&
                  Array.isArray(value) && (
                    <MultiSelect
                      attribute={field}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      ref={ref}
                      autoFocus={field._id === autoFocusAttributeId}
                    />
                  )}

                {field.__typename === 'SingleSelectAttribute' &&
                  (typeof value === 'string' || value === null) && (
                    <SingleSelect
                      attribute={field}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      ref={ref}
                      autoFocus={field._id === autoFocusAttributeId}
                    />
                  )}

                {field.__typename === 'FreeTextAttribute' &&
                  typeof value === 'string' && (
                    <FreeText
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      ref={ref}
                      autoFocus={field._id === autoFocusAttributeId}
                    />
                  )}

                {isAllowedViewersVisible && (
                  <AllowedViewers
                    allowedViewers={field.allowedViewers}
                    ownerKind={field.ownerKind}
                    isViewingOwnProfile={isViewingOwnProfile}
                    allowedEditors={field.allowedEditors}
                    canCurrentUserEditValue={field.canCurrentUserEditValue}
                  />
                )}

                {field.isControlledByExternalAMSField ? (
                  <FormHelperText>
                    This field is controlled by your {association} account
                  </FormHelperText>
                ) : !isAllowedViewersVisible &&
                  !field.canCurrentUserEditValue ? (
                  <FormHelperText>
                    You do not have permission to edit this field
                  </FormHelperText>
                ) : field.label === 'Account Email' ? (
                  <FormHelperText>
                    The email you will use to log in to Tradewing
                  </FormHelperText>
                ) : null}

                <FormErrorMessage>{error?.message}</FormErrorMessage>
              </FormControl>
            )}
          />
        );
      })}
    </Stack>
  );
};
